import { useState, useCallback, useRef } from "react";
import { searchRecipients } from "./api";

// Manages the active Stage 1 search's state: query, results, per-row
// checkbox set, loading/error. Search is invoked imperatively (via the
// Search button) rather than debounced — the recipient picker's
// semantic-mode search is OpenAI-backed, and we don't want a keystroke
// to fire a paid API call.
//
// Checked-by-default policy: every row in the latest result set is
// checked initially. Admin unchecks the ones they don't want before
// committing to the pool. Outlet-mode flattens grouped reporters into
// the checked set.
//
// resetQuery() clears query + results + checked (used when leaving the
// modal or after "Add N & search again"). Mode/filters are NOT reset
// here — the parent (RecipientPicker) owns those so they persist across
// searches.
export default function useSearchResults({ searchFn = searchRecipients } = {}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ rows: [], pagination: {} });
  const [checked, setChecked] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryAdvised, setRetryAdvised] = useState(false);
  const abortRef = useRef(null);

  // Flatten the rows to ids regardless of shape (paginated list vs
  // outlet-grouped). Outlet-grouped rows are { outlet, reporters } —
  // reporters is the recipient list. Paginated rows are flat.
  const extractIds = (rows) => rows.flatMap((row) => (row.reporters ? row.reporters.map((r) => r.id) : [row.id]));

  const runSearch = useCallback(
    async ({ mode }) => {
      const trimmed = query.trim();
      if (!trimmed) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      setRetryAdvised(false);
      try {
        const data = await searchFn({
          mode,
          query: trimmed,
          signal: controller.signal,
        });
        setResults(data);
        setChecked(new Set(extractIds(data.rows || [])));
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Search failed");
        setRetryAdvised(Boolean(err.retryAdvised));
        setResults({ rows: [], pagination: {} });
        setChecked(new Set());
      } finally {
        if (abortRef.current === controller) {
          setLoading(false);
          abortRef.current = null;
        }
      }
    },
    [query, searchFn],
  );

  const toggleCheck = useCallback((id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Set the checked set for a batch of ids (used by per-outlet
  // "Select all" / "Deselect all" affordances).
  const setCheckedForIds = useCallback((ids, isChecked) => {
    setChecked((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (isChecked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const checkedRows = useCallback(() => {
    const flat = (results.rows || []).flatMap((row) => (row.reporters ? row.reporters : [row]));
    return flat.filter((r) => checked.has(r.id));
  }, [results.rows, checked]);

  const resetQuery = useCallback(() => {
    abortRef.current?.abort();
    setQuery("");
    setResults({ rows: [], pagination: {} });
    setChecked(new Set());
    setError(null);
    setRetryAdvised(false);
  }, []);

  return {
    query,
    setQuery,
    results,
    checked,
    checkedRows,
    toggleCheck,
    setCheckedForIds,
    loading,
    error,
    retryAdvised,
    runSearch,
    resetQuery,
  };
}
